import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, Check, AlertCircle } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function SigningPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signatureRequest, setSignatureRequest] = useState(null);
  const [error, setError] = useState('');
  const [signatureType, setSignatureType] = useState('draw'); // 'draw' or 'type'
  const [typedName, setTypedName] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    loadSigningRequest();
  }, [token]);

  useEffect(() => {
    if (signatureType === 'draw' && canvasRef.current) {
      setupCanvas();
    }
  }, [signatureType]);

  const loadSigningRequest = async () => {
    try {
      const response = await api.get(`/signatures/sign/${token}`);
      setSignatureRequest(response.data);
      setTypedName(response.data.signer_name);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired signing link');
    } finally {
      setLoading(false);
    }
  };

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const generateTypedSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = '48px "Brush Script MT", cursive';
    ctx.fillStyle = '#000';
    ctx.textAlign = 'center';
    ctx.fillText(typedName, canvas.width / 2, canvas.height / 2 + 15);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Generate signature if typed
    if (signatureType === 'type') {
      generateTypedSignature();
    }

    // Get signature data from canvas
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');

    // Check if canvas is blank
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const isBlank = !imageData.data.some(channel => channel !== 0);

    if (isBlank) {
      toast.error('Please provide a signature');
      return;
    }

    setSigning(true);
    try {
      await api.post('/signatures/submit', {
        token,
        signature_data: signatureData
      });

      toast.success('Document signed successfully!');
      setTimeout(() => {
        // Redirect to a success page or close window
        window.location.href = 'https://corpcraft.io';
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit signature');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-red-500 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Invalid Link</h2>
          <p className="text-slate-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Sign Document</h1>
              <p className="text-slate-400 text-sm">Please review and sign below</p>
            </div>
          </div>

          {/* Document Info */}
          <div className="bg-slate-900/50 rounded-lg p-6 mb-6">
            <h3 className="font-semibold mb-4">Document Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Document:</span>
                <span className="font-medium">{signatureRequest?.document_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Signer:</span>
                <span className="font-medium">{signatureRequest?.signer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Expires:</span>
                <span className="font-medium">{signatureRequest?.expires_at}</span>
              </div>
            </div>
          </div>

          {/* Document Preview (placeholder) */}
          <div className="bg-white rounded-lg p-6 mb-6 text-slate-900">
            <h4 className="font-bold text-lg mb-4">{signatureRequest?.document_name}</h4>
            <p className="text-sm mb-4">
              This is a formation document for the corporate entity.
            </p>
            <p className="text-sm mb-4">
              By signing below, you acknowledge that you have reviewed and accept the terms outlined in this document.
            </p>
            <p className="text-sm text-slate-600">
              [Full document content would appear here in production]
            </p>
          </div>

          {/* Signature Section */}
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <Label className="mb-3 block">Signature Type</Label>
              <div className="flex gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => setSignatureType('draw')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    signatureType === 'draw'
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  ✍️ Draw Signature
                </button>
                <button
                  type="button"
                  onClick={() => setSignatureType('type')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    signatureType === 'type'
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  ⌨️ Type Name
                </button>
              </div>

              {signatureType === 'type' && (
                <div className="mb-4">
                  <Input
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Enter your full legal name"
                    className="text-lg"
                  />
                </div>
              )}

              {/* Canvas */}
              <div className="bg-white rounded-lg p-4 mb-4">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  className="border border-slate-300 rounded cursor-crosshair w-full"
                  style={{ touchAction: 'none' }}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={clearCanvas}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  Clear
                </Button>
                {signatureType === 'type' && (
                  <Button
                    type="button"
                    onClick={generateTypedSignature}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    Preview Signature
                  </Button>
                )}
              </div>
            </div>

            {/* Consent */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-300">
                By clicking "Sign Document" below, I agree that this electronic signature is the legal equivalent
                of my handwritten signature and I intend to be bound by this document.
              </p>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={signing}
              className="w-full gradient-accent hover:opacity-90 h-12 text-lg"
            >
              {signing ? 'Submitting...' : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Sign Document
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-slate-400 mt-6">
          Powered by CorpCraft - Secure Electronic Signatures
        </p>
      </div>
    </div>
  );
}
